<script setup>
import { useAnaesthetistStore } from '@/stores/anaesthetist'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

// Import components
import HospitalsList from '@/views/pages/anaesthetist/HospitalsList.vue'
import CalendarSchedule from '@/views/pages/anaesthetist/CalendarSchedule.vue'
import ProfileDetails from '@/views/pages/anaesthetist/ProfileDetails.vue'
import SpecialistAreasList from '@/views/pages/anaesthetist/SpecialistAreasList.vue'
import AvailabilitySchedule from '@/views/pages/anaesthetist/AvailabilitySchedule.vue'
import AvailabilityExceptions from '@/views/pages/anaesthetist/AvailabilityExceptions.vue'
import SecretaryAssociationTab from '@/views/pages/anaesthetist/SecretaryAssociationTab.vue'

const anaesthetistStore = useAnaesthetistStore()
const authStore = useAuthStore()

const { currentAnaesthetist } = storeToRefs(anaesthetistStore)
const { currentUser, anesthesiologistData } = storeToRefs(authStore)

const route = useRoute('anaesthetist-id-tab')
const anaesthetistId = computed(() => route.params.id)
const isCreateMode = computed(() => anaesthetistId.value === 'new')

const resolvedAnaesthetistId = computed(() => {
  if (anaesthetistId.value !== 'me')
    return anaesthetistId.value

  return (
    anesthesiologistData.value?.id
    || currentUser.value?.userable_id
    || currentUser.value?.userable?.id
    || currentUser.value?.id
  )
})

const activeTab = computed({
  get: () => route.params.tab || 'profile',
  set: () => route.params.tab,
})

const ensureAnaesthetist = id => {
  if (isCreateMode.value) {
    anaesthetistStore.startNewAnaesthetist()

    return
  }

  if (!id)
    return

  if (currentAnaesthetist.value?.id !== id)
    anaesthetistStore.fetchAnaesthetist(id)
}

onMounted(() => {
  ensureAnaesthetist(resolvedAnaesthetistId.value)
})

watch([anaesthetistId, resolvedAnaesthetistId], () => {
  ensureAnaesthetist(resolvedAnaesthetistId.value)
})

onUnmounted(() => {
  anaesthetistStore.reset()
})

const tabs = [
  {
    title: 'Personal details',
    icon: 'ri-user-line',
    tab: 'profile',
    component: 'ProfileDetails',
  },
  {
    title: 'Hospitals',
    icon: 'ri-hospital-line',
    tab: 'hospitals',
    component: 'HospitalsList',
  },
  {
    title: 'Specialist areas',
    icon: 'ri-stethoscope-line',
    tab: 'specialist-areas',
    component: 'SpecialistAreasList',
  },
  {
    title: 'Availability',
    icon: 'ri-time-line',
    tab: 'availability',
    component: 'AvailabilitySchedule',
  },
  {
    title: 'Exceptions',
    icon: 'ri-forbid-2-line',
    tab: 'exceptions',
    component: 'AvailabilityExceptions',
  },
  {
    title: 'Calendar',
    icon: 'ri-calendar-event-line',
    tab: 'calendar',
    component: 'CalendarSchedule',
  },
  {
    title: 'Secretary group',
    icon: 'ri-team-line',
    tab: 'secretary-group',
    component: 'SecretaryAssociationTab',
    anaesthetistOnly: true,
  },
]

const isAnaesthetistUser = computed(() => {
  const role = currentUser.value?.role || ''
  
  return role === 'anaesthetist'
})

const visibleTabs = computed(() =>
  tabs.filter(tab => !tab.anaesthetistOnly || isAnaesthetistUser.value),
)

const componentMap = {
  ProfileDetails,
  HospitalsList,
  SpecialistAreasList,
  AvailabilitySchedule,
  AvailabilityExceptions,
  CalendarSchedule,
  SecretaryAssociationTab,
}

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
  },
})
</script>

<template>
  <div>
    <div
      v-if="anaesthetistStore.loading && !currentAnaesthetist"
      class="text-center py-8"
    >
      <VProgressCircular indeterminate />
    </div>

    <div v-else>
      <VAlert
        v-if="anaesthetistStore.error"
        type="error"
        class="mb-6"
      >
        {{ anaesthetistStore.error }}
      </VAlert>

      <VCard class="mb-6">
        <VCardText>
          <div class="d-flex align-center gap-4">
            <VAvatar size="64">
              <span class="text-h5">
                {{ currentAnaesthetist?.personal_data?.first_name?.charAt(0) || 'N' }}
              </span>
            </VAvatar>
            <div>
              <h2 class="text-h5 font-weight-bold">
                {{ isCreateMode ? 'New Anaesthetist' : anaesthetistStore.fullName || 'Anaesthetist' }}
              </h2>
              <p class="text-body-2 text-medium-emphasis">
                GMC: {{ currentAnaesthetist?.gmc_number || '—' }}
              </p>
            </div>
          </div>
        </VCardText>
      </VCard>

      <VTabs v-model="activeTab">
        <VTab
          v-for="item in visibleTabs"
          :key="item.tab"
          :value="item.tab"
          :to="{
            name: 'anaesthetist-id-tab',
            params: {
              id: anaesthetistId,
              tab: item.tab,
            },
          }"
          :disabled="isCreateMode && item.tab !== 'profile'"
        >
          <VIcon
            start
            :icon="item.icon"
          />
          {{ item.title }}
        </VTab>
      </VTabs>

      <VWindow
        v-model="activeTab"
        class="disable-tab-transition"
        :touch="false"
      >
        <VWindowItem
          v-for="item in visibleTabs"
          :key="item.tab"
          :value="item.tab"
        >
          <component :is="componentMap[item.component]" />
        </VWindowItem>
      </VWindow>
    </div>
  </div>
</template>
